import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import {
  UserManager,
  WebStorageStateStore,
  type INavigator,
  type IWindow,
  type User,
} from "oidc-client-ts";

import { setTokenSource } from "../api/apiClient.js";
import { frontendConfig } from "../api/config.js";

/**
 * Anmeldung über den zentralen Keycloak auf auth.community-project.de, Realm
 * `community` — ein Konto über alle Apps hinweg. Authorization Code mit PKCE, ohne
 * Client-Secret: die Oberfläche ist ein öffentlicher Client.
 *
 * Rücksprung-Adresse bewusst **nicht** unter /auth: dort läge hinter einem Reverse
 * Proxy der Identity Provider selbst, und der Rücksprung landete bei Keycloak.
 */
export const CALLBACK_PATH = "/oidc/callback";
export const NATIVE_CALLBACK_URL = "com.community.project.bookking://oidc/callback";

/**
 * Der angemeldete Benutzer, außerhalb von React gehalten.
 *
 * Der API-Client braucht das Zugriffstoken synchron. Käme es aus einem Effekt der
 * Elternkomponente, wäre es zu spät: React führt die Effekte der Kinder zuerst aus, und
 * die erste Abfrage ginge ohne Token hinaus.
 */
let currentUser: User | null = null;

setTokenSource(() => (currentUser && !currentUser.expired ? currentUser.access_token : null));

export function rememberUser(user: User | null): void {
  currentUser = user;
}

export async function registerAccount(): Promise<void> {
  await userManager().signinRedirect({ prompt: "create" });
}

let manager: UserManager | null = null;

// In der App öffnet sich das Anmeldefenster im System-Browser: nur dort gilt eine
// bestehende Keycloak-Sitzung, und nur dort sieht der Benutzer die Adresszeile.
const nativeBrowserNavigator: INavigator = {
  async prepare(): Promise<IWindow> {
    return {
      async navigate({ url }) {
        await Browser.open({ url });
        return { url };
      },
      close() {
        void Browser.close();
      },
    };
  },
  async callback(): Promise<void> {},
};

export function userManager(): UserManager {
  if (!manager) {
    const config = frontendConfig();
    const origin = window.location.origin;
    const redirectUri = Capacitor.isNativePlatform()
      ? NATIVE_CALLBACK_URL
      : `${origin}${CALLBACK_PATH}`;
    manager = new UserManager(
      {
        authority: config.oidcIssuer,
        client_id: config.oidcClientId,
        redirect_uri: redirectUri,
        post_logout_redirect_uri: origin,
        response_type: "code",
        scope: "openid profile email",
        automaticSilentRenew: true,
        userStore: new WebStorageStateStore({ store: window.localStorage }),
      },
      Capacitor.isNativePlatform() ? nativeBrowserNavigator : undefined,
    );
  }
  return manager;
}

/**
 * Verarbeitet den Rücksprung von Keycloak — und zwar **genau einmal**.
 *
 * Der Autorisierungscode ist ein Einmal-Ticket: der zweite Einlöseversuch scheitert mit
 * 400. React startet Effekte im StrictMode aber absichtlich doppelt, und ohne diese
 * Absicherung machte der zweite Lauf die Anmeldung des ersten wieder zunichte.
 */
let pendingCallback: Promise<User | null> | null = null;

export function isCallbackUrl(url = window.location.href): boolean {
  if (url.startsWith(NATIVE_CALLBACK_URL)) {
    return true;
  }
  return new URL(url).pathname === CALLBACK_PATH;
}

export function completeSignin(url?: string): Promise<User | null> {
  pendingCallback ??= (async () => {
    try {
      const user = await userManager().signinCallback(url);
      return user ?? (await userManager().getUser());
    } finally {
      if (Capacitor.isNativePlatform()) {
        void Browser.close();
      }
      // Code und State aus der Adresszeile entfernen, damit ein Neuladen nicht scheitert.
      window.history.replaceState({}, "", "/");
    }
  })();
  return pendingCallback;
}
